import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarAlt,
  faNoteSticky,
  faCircleCheck,
  faCircleExclamation,
  faCircleInfo,
  faChevronDown,
} from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";

const ValorationStateEalthHistory = ({ data, isLoading, title }) => {
  const { t, i18n } = useTranslation();

  const normalizeData = (obj) => {
    const valueMaps = {
      es: {
        Good: "Bueno",
        Fair: "Regular",
        Unable: "Incapaz",
        Excellent: "Excelente",
        Average: "Promedio",
        Poor: "Deficiente",
        "Very low": "Muy bajo",
        Normal: "Normal",
        Limited: "Limitada",
        "Severely reduced": "Severamente reducida",
      },
      en: {
        Good: "Good",
        Fair: "Fair",
        Unable: "Unable",
        Excellent: "Excellent",
        Average: "Average",
        Poor: "Poor",
        "Very low": "Very low",
        Normal: "Normal",
        Limited: "Limited",
        "Severely reduced": "Severely reduced",
      },
      pt: {
        Good: "Bom",
        Fair: "Regular",
        Unable: "Incapaz",
        Excellent: "Excelente",
        Average: "Médio",
        Poor: "Ruim",
        "Very low": "Muito baixo",
        Normal: "Normal",
        Limited: "Limitada",
        "Severely reduced": "Severamente reduzida",
      },
    };

    const valueMap = valueMaps[i18n.language as keyof typeof valueMaps] || valueMaps.es;
    return valueMap[obj] || obj || t("history.no_data");
  };

  const fieldLabels = {
    main_answer: t("history.fields.main_answer"),
    knee_alignment: t("history.fields.knee_alignment"),
    pelvic_stability: t("history.fields.pelvic_stability"),
    ankle_mobility: t("history.fields.ankle_mobility"),
    squat_depth: t("history.fields.squat_depth"),
    postural_control: t("history.fields.postural_control"),
    body_axis: t("history.fields.body_axis"),
    spinal_curvature: t("history.fields.spinal_curvature"),
    head_position: t("history.fields.head_position"),
    pelvic_tilt: t("history.fields.pelvic_tilt"),
    knee_flexion: t("history.fields.knee_flexion"),
    shoulder_symmetry: t("history.fields.shoulder_symmetry"),
    spinal_alignment: t("history.fields.spinal_alignment"),
    pelvic_level: t("history.fields.pelvic_level"),
    knee_valgus_varus: t("history.fields.knee_valgus_varus"),
    foot_arch: t("history.fields.foot_arch"),
    head_rotation: t("history.fields.head_rotation"),
    scapular_rotation: t("history.fields.scapular_rotation"),
    pelvic_torsion: t("history.fields.pelvic_torsion"),
    knee_foot_orientation: t("history.fields.knee_foot_orientation"),
    trunk_control: t("history.fields.trunk_control"),
    hip_mobility: t("history.fields.hip_mobility"),
    spinal_mobility: t("history.fields.spinal_mobility"),
    foot_alignment: t("history.fields.foot_alignment"),
    compensatory_rotations: t("history.fields.compensatory_rotations"),
    control_motor: t("history.fields.control_motor"),
    hip_hinge_pattern: t("history.fields.hip_hinge_pattern"),
    stability_mobility: t("history.fields.stability_mobility"),
    observations: t("history.fields.observations"),
    created_at: t("history.fields.created_at"),
    updated_at: t("history.fields.updated_at"),
    evaluation_date: t("history.fields.evaluation_date"),
  };

  const getValueStyle = (value) => {
    switch (normalizeData(value)) {
      case t("history.values.good"):
      case t("history.values.excellent"):
      case t("history.values.normal"):
        return "text-green-600";
      case t("history.values.fair"):
      case t("history.values.average"):
      case t("history.values.limited"):
        return "text-yellow-600";
      case t("history.values.unable"):
      case t("history.values.poor"):
      case t("history.values.very_low"):
      case t("history.values.severely_reduced"):
        return "text-red-600";
      default:
        return "text-gray-600";
    }
  };

  const getValueIcon = (value) => {
    switch (normalizeData(value)) {
      case t("history.values.good"):
      case t("history.values.excellent"):
      case t("history.values.normal"):
        return faCircleCheck;
      case t("history.values.fair"):
      case t("history.values.average"):
      case t("history.values.limited"):
        return faCircleInfo;
      case t("history.values.unable"):
      case t("history.values.poor"):
      case t("history.values.very_low"):
      case t("history.values.severely_reduced"):
        return faCircleExclamation;
      default:
        return faNoteSticky;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return t("history.no_date");
    return new Date(dateStr).toLocaleString(i18n.language, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  };

  const TimelineNode = ({ node, index }) => {
    const [isOpen, setIsOpen] = useState(false);

    if (!node) return null;

    return (
      <div className="relative pl-12">
        <motion.div
          className={`absolute left-4 top-0 w-1 ${
            index === 0 ? "rounded-t" : ""
          } ${index === (data?.length || 0) - 1 ? "h-12" : "h-full"}`}
          initial={{ backgroundColor: "#e5e7eb" }}
          animate={{
            backgroundColor: isOpen ? "primary" : "primary",
          }}
          transition={{ duration: 0.5, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute left-1.5 top-2 w-6 h-6 bg-primary rounded-full flex items-center justify-center cursor-pointer"
          onClick={() => setIsOpen(!isOpen)}
          animate={{ rotate: isOpen ? 0 : -90 }}
          transition={{ duration: 0.2 }}
        >
          <FontAwesomeIcon
            icon={faChevronDown}
            className="text-white text-xs"
          />
        </motion.div>
        <div className="ml-4 pb-8">
          <div
            className="flex items-center gap-2 cursor-pointer"
            onClick={() => setIsOpen(!isOpen)}
          >
            <FontAwesomeIcon icon={faCalendarAlt} className="text-primary" />
            <span className="font-semibold text-gray-800">
              {formatDate(node.evaluation_date)}
            </span>
          </div>
          <AnimatePresence>
            {isOpen && (
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
                className="mt-4"
              >
                {Object.entries(node)
                  .filter(
                    ([key]) =>
                      key !== "id" &&
                      key !== "evaluation_id" &&
                      key !== "evaluation_user_id" &&
                      key !== "created_at" &&
                      key !== "updated_at"
                  )
                  .map(([key, value]) => (
                    <div key={key} className="flex items-start gap-3 py-2">
                      <FontAwesomeIcon
                        icon={
                          key === "observations"
                            ? faNoteSticky
                            : key.includes("date")
                              ? faCalendarAlt
                              : getValueIcon(value)
                        }
                        className={`mt-1 ${
                          key === "observations" || key.includes("date")
                            ? "text-gray-500"
                            : getValueStyle(value)
                        }`}
                      />
                      <div>
                        <span className="font-medium text-gray-700">
                          {fieldLabels[key] || key}:
                        </span>
                        <span
                          className={`ml-2 ${
                            key === "observations"
                              ? "text-gray-600"
                              : getValueStyle(value)
                          }`}
                        >
                          {["evaluation_date"].includes(key)
                            ? formatDate(value)
                            : normalizeData(value)}
                        </span>
                      </div>
                    </div>
                  ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    );
  };

  return (
    <div className="p-6">
      {isLoading ? (
        <div className="text-gray-600 text-center">
          <svg
            className="animate-spin h-6 w-6 mx-auto text-primary"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8v8h8a8 8 0 01-16 0z"
            />
          </svg>
          <span className="mt-2 block">{t("history.loading")}</span>
        </div>
      ) : data && Array.isArray(data) && data.length > 0 ? (
        <>
          <h3 className="text-2xl font-bold text-gray-900 mb-6 border-l-4 border-primary pl-3">
            {title || t("history.title")}
          </h3>
          <div className="relative">
            {[...data]
              .filter((entry) => entry && typeof entry === "object")
              .sort((a, b) => {
                const dateA = a?.evaluation_date
                  ? new Date(a.evaluation_date)
                  : new Date(0);
                const dateB = b?.evaluation_date
                  ? new Date(b.evaluation_date)
                  : new Date(0);
                return dateB - dateA;
              })
              .map((entry, index) => (
                <TimelineNode
                  key={entry?.id || index}
                  node={entry}
                  index={index}
                />
              ))}
          </div>
        </>
      ) : (
        <div className="text-gray-600 text-center py-8">
          <FontAwesomeIcon
            icon={faCircleInfo}
            className="text-gray-400 text-2xl mb-2"
          />
          <p>{t("history.no_data_available")}</p>
        </div>
      )}
    </div>
  );
};

export default ValorationStateEalthHistory;